import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useState } from 'react';
import { useAITraces } from '../ai-tracer-integration/hooks/useAITracerData';
import { useTraceTableData } from './hooks/use-trace-table-data';
import type { SortDirection } from './hooks/use-trace-table-data';
import { TraceDetailModal } from './llm-traces/trace-detail-modal';
import { TraceTableCell } from './llm-traces/trace-table-cell';
import {
	FIELD_CONFIGS,
	type SortableFields,
	type TraceRow,
} from './llm-traces/trace-table-config';
import {
	DateRangeFilter,
	calculateDateRange,
} from './shared/date-range-filter';
import { PaginationControls } from './shared/pagination-controls';

interface LLMTracesProps {
	environmentFilter?: string;
}

export const LLMTraces = ({ environmentFilter }: LLMTracesProps) => {
	// State management
	const [selectedTrace, setSelectedTrace] = useState<TraceRow | null>(null);
	const [traceFamily, setTraceFamily] = useState<{
		rootTrace: TraceRow;
		selectedTraceId: string;
	} | null>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [globalFilter, setGlobalFilter] = useState('');
	const [expandedTraces, setExpandedTraces] = useState<Set<string>>(new Set());
	const [sessionFilter, setSessionFilter] = useState<string>('');
	const [sortField, setSortField] = useState<SortableFields>('created_at');
	const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

	// Date range filtering state
	const [dateRange, setDateRange] = useState<string>('30d'); // Default to past 30 days
	// Calculate initial date range immediately (30 days back from today)
	const initialDateRange = calculateDateRange(30);
	const [dateFrom, setDateFrom] = useState<string | undefined>(
		initialDateRange.dateFrom,
	);
	const [dateTo, setDateTo] = useState<string | undefined>(
		initialDateRange.dateTo,
	);

	const { traces, isLoading, isLoadingMore, error, chunksLoaded, totalChunks } =
		useAITraces(environmentFilter, 1000, dateFrom, dateTo);

	// Data processing using custom hook
	const { filteredData, paginatedData, totalPages, displayData } =
		useTraceTableData({
			traces,
			isLoading,
			filters: {
				global: globalFilter,
				session: sessionFilter,
			},
			sorting: {
				field: sortField,
				direction: sortDirection,
			},
			pagination: {
				currentPage,
				pageSize,
			},
			expandedTraces,
		});

	// Helper function to find the root trace for a given trace
	const findRootTrace = (trace: TraceRow, allTraces: TraceRow[]): TraceRow => {
		// If this trace has a parent_span_id, search for its root
		if (trace.parent_span_id) {
			// Find all potential root traces
			for (const rootCandidate of allTraces) {
				// Check if this trace exists in the rootCandidate's hierarchy
				const found = findTraceInHierarchy(rootCandidate, trace.id);
				if (found) {
					return rootCandidate;
				}
			}
		}
		// If no parent found or no parent_span_id, this is the root
		return trace;
	};

	// Helper function to search for a trace in a hierarchy
	const findTraceInHierarchy = (
		rootTrace: TraceRow,
		targetId: string,
	): TraceRow | null => {
		if (rootTrace.id === targetId) return rootTrace;

		if (rootTrace.children && rootTrace.children.length > 0) {
			for (const child of rootTrace.children) {
				const found = findTraceInHierarchy(child, targetId);
				if (found) return found;
			}
		}
		return null;
	};

	// Event handlers
	const toggleTraceExpansion = (traceId: string) => {
		const newExpanded = new Set(expandedTraces);
		if (expandedTraces.has(traceId)) {
			newExpanded.delete(traceId);
		} else {
			newExpanded.add(traceId);
		}
		setExpandedTraces(newExpanded);
	};

	const handleTraceClick = (trace: TraceRow) => {
		// Find the root trace for this trace
		const rootTrace = findRootTrace(trace, traces);

		// If this is a child trace (has a different root), pass the family context
		if (rootTrace.id !== trace.id) {
			setTraceFamily({
				rootTrace,
				selectedTraceId: trace.id,
			});
			setSelectedTrace(rootTrace); // Set root as the trace prop
		} else {
			// This is already a root trace
			setTraceFamily(null);
			setSelectedTrace(trace);
		}
	};

	const handleCloseModal = () => {
		setSelectedTrace(null);
		setTraceFamily(null);
	};

	const handleSessionClick = (sessionId: string) => {
		setSessionFilter(sessionId);
		setCurrentPage(0);
	};

	const clearSessionFilter = () => {
		setSessionFilter('');
		setCurrentPage(0);
	};

	const handleDateRangeChange = (
		range: string,
		dateFrom?: string,
		dateTo?: string,
	) => {
		setDateRange(range);
		setDateFrom(dateFrom);
		setDateTo(dateTo);
		setCurrentPage(0); // Reset pagination when date range changes
	};

	const handleSort = (field: SortableFields) => {
		if (sortField === field) {
			// Toggle direction if same field
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			// New field, default to desc for most fields, asc for text fields
			setSortField(field);
			setSortDirection(
				['function_id', 'model_name', 'environment', 'status'].includes(field)
					? 'asc'
					: 'desc',
			);
		}
		setCurrentPage(0);
	};

	// Sortable header component
	const SortableHeader = ({
		field,
		children,
		className = '',
	}: {
		field?: SortableFields;
		children: React.ReactNode;
		className?: string;
	}) => {
		if (!field) {
			return <TableHead className={className}>{children}</TableHead>;
		}

		const isActive = sortField === field;
		const direction = isActive ? sortDirection : null;

		return (
			<TableHead
				className={`${className} tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none`}
				onClick={() => handleSort(field)}
			>
				<div className='tw-flex tw-items-center tw-justify-center tw-gap-1'>
					{children}
					<div className='tw-flex tw-flex-col'>
						<ChevronUp
							className={`tw-h-3 tw-w-3 ${isActive && direction === 'asc' ? 'tw-text-blue-600' : 'tw-text-gray-300'}`}
						/>
						<ChevronDown
							className={`tw-h-3 tw-w-3 -tw-mt-1 ${isActive && direction === 'desc' ? 'tw-text-blue-600' : 'tw-text-gray-300'}`}
						/>
					</div>
				</div>
			</TableHead>
		);
	};

	// Handle GraphQL errors
	if (error) {
		return (
			<div className='tw-space-y-4'>
				<div className='tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-4'>
					<div className='tw-flex'>
						<div className='tw-ml-3'>
							<h3 className='tw-text-sm tw-font-medium tw-text-red-800'>
								Error loading AI traces
							</h3>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='tw-space-y-4'>
			{/* Filters and Search */}
			<div className='tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-center tw-justify-between'>
				<div className='tw-flex tw-gap-4 tw-flex-1'>
					<div className='tw-relative tw-w-full sm:tw-w-72'>
						<Search className='tw-absolute tw-left-2 tw-top-2.5 tw-h-4 tw-w-4 tw-text-muted-foreground' />
						<Input
							placeholder='Search traces...'
							value={globalFilter ?? ''}
							onChange={(e) => setGlobalFilter(e.target.value)}
							className='tw-pl-8'
						/>
					</div>
					{sessionFilter && (
						<div className='tw-flex tw-items-center tw-gap-2 tw-bg-blue-50 tw-px-3 tw-py-1 tw-rounded-md'>
							<span className='tw-text-sm tw-text-blue-700'>
								Session: {sessionFilter}
							</span>
							<Button
								variant='ghost'
								size='sm'
								onClick={clearSessionFilter}
								className='tw-h-6 tw-w-6 tw-p-0 tw-text-blue-600 hover:tw-text-blue-800'
							>
								✕
							</Button>
						</div>
					)}
				</div>
				<div className='tw-text-sm tw-text-muted-foreground tw-flex tw-items-center tw-gap-2'>
					{filteredData.length} traces available
					{isLoadingMore && totalChunks > 0 && (
						<span className='tw-text-blue-600 tw-text-xs'>
							Loading... ({chunksLoaded}/{totalChunks} chunks)
						</span>
					)}
				</div>
			</div>

			{/* Date Range Filter */}
			<DateRangeFilter
				selectedRange={dateRange}
				onRangeChange={handleDateRangeChange}
				className='tw-border-t tw-pt-4'
			/>

			{/* Data Table */}
			<div className='tw-rounded-md tw-border tw-w-full overflow-x-auto'>
				<Table>
					<TableHeader>
						<TableRow>
							{FIELD_CONFIGS.map((fieldConfig, index) => (
								<SortableHeader
									key={fieldConfig.key || index}
									field={fieldConfig.key || undefined}
									className='tw-text-center tw-font-medium'
								>
									{fieldConfig.label}
								</SortableHeader>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: pageSize }).map((_, index) => (
								<TableRow key={`loading-${index}`}>
									{FIELD_CONFIGS.map((_, cellIndex) => (
										<TableCell key={`loading-cell-${cellIndex}`}>
											<div className='tw-animate-pulse tw-bg-gray-200 tw-h-4 tw-rounded' />
										</TableCell>
									))}
								</TableRow>
							))
						) : displayData.length > 0 ? (
							displayData.map((trace) => {
								const hasChildren = trace.children && trace.children.length > 0;
								const isExpanded = expandedTraces.has(trace.id);
								return (
									<TableRow
										key={trace.id}
										className={`tw-cursor-pointer hover:tw-bg-gray-50 ${
											trace.isChild ? 'tw-bg-blue-25 tw-pl-4' : ''
										}`}
										onClick={() => handleTraceClick(trace)}
									>
										{FIELD_CONFIGS.map((fieldConfig, cellIndex) => (
											<TableCell
												key={fieldConfig.key || cellIndex}
												className={`tw-text-center ${fieldConfig.className || ''}`}
												title={
													fieldConfig.key === 'model_name'
														? trace.model_name
														: undefined
												}
											>
												<TraceTableCell
													fieldConfig={fieldConfig}
													trace={trace}
													cellIndex={cellIndex}
													hasChildren={hasChildren ?? false}
													isExpanded={isExpanded}
													onToggleExpansion={toggleTraceExpansion}
													onSessionClick={handleSessionClick}
												/>
											</TableCell>
										))}
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={FIELD_CONFIGS.length}
									className='tw-h-24 tw-text-center'
								>
									No traces found
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			{/* Pagination */}
			<PaginationControls
				currentPage={currentPage}
				totalPages={totalPages}
				pageSize={pageSize}
				totalItems={filteredData.length}
				displayedItems={paginatedData.length}
				onPageChange={setCurrentPage}
				onPageSizeChange={setPageSize}
				itemName='parent traces'
			/>

			{/* Detail Modal */}
			<TraceDetailModal
				trace={selectedTrace}
				traceFamily={traceFamily}
				onClose={handleCloseModal}
			/>
		</div>
	);
};

export default LLMTraces;
